import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useTableSort } from "@/lib/tableSort";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { GitCompare, Search, Radar as RadarIcon, Download, FileText } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { PREDICTION_LABELS_NUM, STATUS_LABELS, ASSIGNABLE_BRANCHES } from "@/lib/constants";

const LC_PRED_OPTIONS = [
  ["KESIN", "Kesin"], ["BELKI", "Belki"], ["GELMEYECEK", "Gelmeyecek"],
  ["ULASILAMADI", "Ulaşılamadı"], ["KARSI", "Karşı"], ["BOS", "İşaretsiz"],
];
const LC_PRED_MAP = Object.fromEntries(LC_PRED_OPTIONS);
const FIELD_LABELS = {
  current_vote_prediction_2026: "Oy eğilimi", status: "Durum",
  proxy_document: "Yetki belgesi", team_assignment_2026: "Sorumlu",
};

const label = (d) => {
  if (d.field === "current_vote_prediction_2026") return (v) => PREDICTION_LABELS_NUM[v] || v || "—";
  if (d.field === "status") return (v) => STATUS_LABELS[v] || v || "—";
  if (d.field === "proxy_document") return (v) => (v ? "Var" : "Yok");
  return (v) => v || "—";
};

export default function LayerConflicts() {
  const lcT = useTableSort([]);
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [branch, setBranch] = useState("all");
  const [onlyVote, setOnlyVote] = useState(true);
  const [centralPred, setCentralPred] = useState("all");
  const [branchPred, setBranchPred] = useState("all");
  const [committee, setCommittee] = useState("all");
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const [reload, setReload] = useState(0);
  const [selected, setSelected] = useState([]);
  const [resolveField, setResolveField] = useState("all");
  const [confirm, setConfirm] = useState(null);
  const [fieldCounts, setFieldCounts] = useState(null);
  const [applyAll, setApplyAll] = useState(false);
  const isBranchManager = user.role === "branch_manager";
  const canResolve = user.role === "super_admin";

  const doResolve = async () => {
    if (!confirm) return;
    setBusy(true);
    try {
      const { data: res } = await api.post("/reports/layer-conflicts/resolve", {
        voter_ids: applyAll ? [] : selected, field: resolveField, source: confirm,
        apply_all: applyAll, branch: branch !== "all" ? branch : null,
      });
      toast.success(`${res.updated} firma eşitlendi (${confirm === "central" ? "merkez esas alındı" : "şube esas alındı"})`);
      setSelected([]);
      setConfirm(null);
      setApplyAll(false);
      setReload((n) => n + 1);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Eşitlenemedi");
    } finally {
      setBusy(false);
    }
  };

  const startApplyAll = (field) => {
    setResolveField(field);
    setApplyAll(true);
    setConfirm("central");
  };

  const download = async (format) => {
    setBusy(true);
    try {
      const params = { format, only_vote: onlyVote };
      if (!isBranchManager && branch !== "all") params.branch = branch;
      if (centralPred !== "all") params.central_pred = centralPred;
      if (branchPred !== "all") params.branch_pred = branchPred;
      if (committee !== "all") params.committee_no = Number(committee);
      const res = await api.get("/reports/layer-conflicts/export", { params, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `katman_farklari.${format === "pdf" ? "pdf" : "xlsx"}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Dışa aktarma başarısız");
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    setData(null);
    const params = { only_vote: onlyVote };
    if (!isBranchManager && branch !== "all") params.branch = branch;
    if (centralPred !== "all") params.central_pred = centralPred;
    if (branchPred !== "all") params.branch_pred = branchPred;
    if (committee !== "all") params.committee_no = Number(committee);
    api.get("/reports/layer-conflicts", { params })
      .then(({ data }) => { setData(data); setSelected([]); })
      .catch(() => toast.error("Katman farkları yüklenemedi"));
    if (canResolve) {
      api.get("/reports/layer-conflicts/field-counts", {
        params: branch !== "all" ? { branch } : {},
      }).then(({ data }) => setFieldCounts(data)).catch(() => setFieldCounts(null));
    }
  }, [branch, onlyVote, centralPred, branchPred, committee, isBranchManager, reload]);

  const rows = useMemo(() => {
    if (!data) return [];
    const t = q.trim().toLocaleLowerCase("tr");
    if (!t) return data.rows;
    return data.rows.filter((r) => `${r.company_name} ${r.registration_no} ${r.contact_person || ""}`.toLocaleLowerCase("tr").includes(t));
  }, [data, q]);

  return (
    <div className="space-y-5" data-testid="layer-conflicts-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="h-11 w-11 rounded-xl grid place-items-center bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-300">
            <GitCompare className="h-5 w-5" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-[#0F2C59] dark:text-sky-300">Katman Farkı Raporu</h1>
            <p className="text-sm text-muted-foreground">Merkez kaydı ile şube kaydı çelişen firmalar</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => download("xlsx")} disabled={busy || !data?.total} data-testid="lc-export-xlsx">
            <Download className="h-4 w-4 mr-1.5" /> Excel
          </Button>
          <Button variant="outline" onClick={() => download("pdf")} disabled={busy || !data?.total} data-testid="lc-export-pdf">
            <FileText className="h-4 w-4 mr-1.5" /> PDF
          </Button>
        </div>
      </div>

      <Card className="p-3 space-y-2">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Firma, sicil no veya kişi ara..." className="pl-9" data-testid="lc-search" />
          </div>
          {!isBranchManager && (
            <Select value={branch} onValueChange={setBranch}>
              <SelectTrigger data-testid="lc-branch-select"><SelectValue placeholder="Şube" /></SelectTrigger>
              <SelectContent className="max-h-[320px]">
                <SelectItem value="all">Tüm Şubeler</SelectItem>
                {ASSIGNABLE_BRANCHES.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
          <Select value={onlyVote ? "vote" : "all"} onValueChange={(v) => setOnlyVote(v === "vote")}>
            <SelectTrigger data-testid="lc-scope-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="vote">Yalnızca oy eğilimi farkı</SelectItem>
              <SelectItem value="all">Tüm alan farkları (sorumlu, durum, belge)</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          <div className="space-y-1">
            <p className="text-[11px] font-medium text-muted-foreground">Komite (merkez)</p>
            <Select value={committee} onValueChange={setCommittee}>
              <SelectTrigger data-testid="lc-committee-select"><SelectValue /></SelectTrigger>
              <SelectContent className="max-h-72">
                <SelectItem value="all">Tüm komiteler</SelectItem>
                {(data?.committees || []).map((c) => (
                  <SelectItem key={c.committee_no} value={String(c.committee_no)}>
                    {c.committee_no}. {c.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <p className="text-[11px] font-medium text-muted-foreground">Merkezdeki eğilim</p>
            <Select value={centralPred} onValueChange={setCentralPred}>
              <SelectTrigger data-testid="lc-central-pred"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Merkez: Tümü</SelectItem>
                {LC_PRED_OPTIONS.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <p className="text-[11px] font-medium text-muted-foreground">Şubedeki eğilim</p>
            <Select value={branchPred} onValueChange={setBranchPred}>
              <SelectTrigger data-testid="lc-branch-pred"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Şube: Tümü</SelectItem>
                {LC_PRED_OPTIONS.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
        {(centralPred !== "all" || branchPred !== "all") && (          <div className="flex items-center gap-2 text-xs" data-testid="lc-active-filter">
            <Badge variant="outline" className="font-normal">
              Merkez: {centralPred === "all" ? "Tümü" : LC_PRED_MAP[centralPred]} → Şube: {branchPred === "all" ? "Tümü" : LC_PRED_MAP[branchPred]}
            </Badge>
            <button type="button" className="text-muted-foreground hover:text-foreground underline"
              onClick={() => { setCentralPred("all"); setBranchPred("all"); }} data-testid="lc-clear-pred">Temizle</button>
          </div>
        )}
        <p className="text-sm text-muted-foreground" data-testid="lc-count">
          {data ? `${data.total} firmada fark var · ${rows.length} kayıt listeleniyor` : "Yükleniyor..."}
        </p>
      </Card>

      {data?.combos?.length > 0 && (
        <Card className="p-3" data-testid="lc-combo-summary">
          <p className="text-sm font-semibold mb-2">Fark Özeti — Merkez → Şube</p>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="text-muted-foreground border-b border-border">
                <tr>
                  <lcT.Th sortKey="lc-merkez" get={(r) => r.center || ""} text className="text-left font-medium py-1.5 px-2">Merkezdeki eğilim</lcT.Th>
                  <lcT.Th sortKey="lc-sube" get={(r) => r.branch || ""} text className="text-left font-medium py-1.5 px-2">Şubedeki eğilim</lcT.Th>
                  <lcT.Th sortKey="lc-firma" get={(r) => r.count ?? 0} className="text-right font-medium py-1.5 px-2">Firma</lcT.Th>
                </tr>
              </thead>
              <tbody>
                {lcT.applySort(data.combos).map((c) => (
                  <tr key={`${c.central}-${c.branch}`}
                    onClick={() => { setCentralPred(c.central); setBranchPred(c.branch); }}
                    className="border-b border-border/50 hover:bg-muted/40 cursor-pointer"
                    data-testid={`lc-combo-${c.central}-${c.branch}`}>
                    <td className="py-1.5 px-2">{LC_PRED_MAP[c.central] || c.central}</td>
                    <td className="py-1.5 px-2 font-medium text-amber-700 dark:text-amber-300">{LC_PRED_MAP[c.branch] || c.branch}</td>
                    <td className="py-1.5 px-2 text-right font-semibold tabular-nums">{c.count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-[11px] text-muted-foreground mt-1.5">Satıra dokununca o kombinasyon filtrelenir.</p>
        </Card>
      )}

      {canResolve && fieldCounts?.total > 0 && (
        <Card className="p-3 border-2 border-[#0F2C59]/30" data-testid="lc-quickfix">
          <p className="text-sm font-semibold mb-1">Tek tuşla eşitle — filtreden bağımsız çalışır</p>
          <p className="text-xs text-muted-foreground mb-2">
            Aşağıdaki düğmeler <b>{branch === "all" ? "tüm şubelerdeki" : branch + " şubesindeki"}</b> o
            alana ait farkların <b>tamamını</b> merkez kaydını esas alarak eşitler. Liste filtresi veya
            satır seçimi gerekmez.
          </p>
          <div className="flex flex-wrap gap-2">
            {Object.entries(fieldCounts.counts).filter(([, n]) => n > 0).map(([f, n]) => (
              <Button key={f} size="sm" variant="outline" className="h-9" disabled={busy}
                onClick={() => startApplyAll(f)} data-testid={`lc-quickfix-${f}`}>
                {FIELD_LABELS[f] || f}
                <Badge className="ml-2 bg-[#0F2C59] text-white">{n}</Badge>
              </Button>
            ))}
          </div>
        </Card>
      )}

      {data?.by_branch?.length > 0 && (
        <Card className="p-3" data-testid="lc-branch-scan">
          <p className="text-sm font-semibold mb-2">Tüm Şubeler Taraması — hangi şubede kaç fark var</p>
          <div className="overflow-x-auto">
            <table className="w-full text-xs" data-testid="lc-branch-scan-table">
              <thead className="text-muted-foreground border-b border-border">
                <tr>
                  <th className="text-left font-medium py-1.5 px-2">Şube</th>
                  <th className="text-right font-medium py-1.5 px-2">Farklı Firma</th>
                  <th className="text-left font-medium py-1.5 px-2">Alan Dağılımı</th>
                </tr>
              </thead>
              <tbody>
                {data.by_branch.map((b) => (
                  <tr key={b.branch} onClick={() => !isBranchManager && setBranch(b.branch)}
                    className={`border-b border-border/50 ${isBranchManager ? "" : "hover:bg-muted/40 cursor-pointer"} ${branch === b.branch ? "bg-amber-50 dark:bg-amber-950/30" : ""}`}
                    data-testid={`lc-branch-scan-${b.branch}`}>
                    <td className="py-1.5 px-2 font-medium">{b.branch || "Merkez"}</td>
                    <td className="py-1.5 px-2 text-right font-semibold tabular-nums text-amber-700 dark:text-amber-300">{b.count}</td>
                    <td className="py-1.5 px-2 text-muted-foreground">
                      {(b.fields || []).map((f) => `${FIELD_LABELS[f.field] || f.field}: ${f.count}`).join(" · ")}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-[11px] text-muted-foreground mt-1.5">
            Satıra dokununca o şube filtrelenir. “Tüm alan farkları” seçeneğiyle durum, sorumlu ve
            belge farklarını da görebilirsiniz.
          </p>
        </Card>
      )}

      {canResolve && selected.length > 0 && (
        <Card className="p-3 border border-amber-500/50 bg-amber-50 dark:bg-amber-950/30 sticky top-2 z-10" data-testid="lc-bulk-bar">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm font-semibold" data-testid="lc-selected-count">{selected.length} firma seçildi</span>
            <Select value={resolveField} onValueChange={setResolveField}>
              <SelectTrigger className="h-9 w-56" data-testid="lc-resolve-field"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Farklı tüm alanlar</SelectItem>
                <SelectItem value="current_vote_prediction_2026">Yalnız oy eğilimi</SelectItem>
                <SelectItem value="status">Yalnız durum (FAAL/ASKI)</SelectItem>
                <SelectItem value="proxy_document">Yalnız yetki belgesi</SelectItem>
                <SelectItem value="team_assignment_2026">Yalnız sorumlu</SelectItem>
              </SelectContent>
            </Select>
            <Button size="sm" className="h-9 bg-[#0F2C59] hover:bg-[#0F2C59]/90" disabled={busy}
              onClick={() => setConfirm("central")} data-testid="lc-resolve-central">
              Merkezi esas al
            </Button>
            <Button size="sm" className="h-9 bg-amber-600 hover:bg-amber-700" disabled={busy}
              onClick={() => setConfirm("branch")} data-testid="lc-resolve-branch">
              Şubeyi esas al
            </Button>
            <button type="button" className="text-xs underline text-muted-foreground hover:text-foreground ml-auto"
              onClick={() => setSelected([])} data-testid="lc-clear-selection">Seçimi temizle</button>
          </div>
        </Card>
      )}

      {!data ? (
        <Skeleton className="h-64 w-full" />      ) : rows.length === 0 ? (        <Card className="p-10 text-center text-sm text-muted-foreground" data-testid="lc-empty">
          Merkez ile şube kayıtları arasında fark bulunamadı.
        </Card>
      ) : (
        <div className="space-y-2">
          {canResolve && (
            <div className="flex items-center gap-2 text-xs" data-testid="lc-select-all-bar">
              <input type="checkbox" className="h-4 w-4 accent-[#0F2C59]"
                checked={rows.length > 0 && rows.every((r) => selected.includes(r.voter_id))}
                onChange={(e) => setSelected(e.target.checked ? rows.map((r) => r.voter_id) : [])}
                data-testid="lc-select-all" />
              <span className="text-muted-foreground">Listelenen {rows.length} firmanın tümünü seç</span>
            </div>
          )}
          {data.by_branch.length > 1 && (
            <div className="flex flex-wrap gap-1.5" data-testid="lc-by-branch">
              {data.by_branch.map((b) => (
                <Badge key={b.branch} variant="secondary" className="text-[11px]">{b.branch}: {b.count}</Badge>
              ))}
            </div>
          )}
          {rows.map((r) => (
            <Card key={r.voter_id} className="p-3" data-testid={`lc-row-${r.voter_id}`}>
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div className="flex items-start gap-2 min-w-0">
                  {canResolve && (
                    <input type="checkbox" className="mt-1 h-4 w-4 accent-[#0F2C59] shrink-0"
                      checked={selected.includes(r.voter_id)}
                      onChange={() => setSelected((s) => s.includes(r.voter_id)
                        ? s.filter((x) => x !== r.voter_id) : [...s, r.voter_id])}
                      data-testid={`lc-check-${r.voter_id}`} />
                  )}
                  <div className="min-w-0">
                    <p className="font-semibold text-sm">{r.company_name}</p>
                    <p className="text-[11px] text-muted-foreground">
                      {r.registration_no ? `Sicil ${r.registration_no} · ` : ""}Komite {r.committee_no} {r.committee_name} · {r.branch}
                      {r.contact_person ? ` · ${r.contact_person}` : ""}
                    </p>
                  </div>
                </div>
                <Button variant="outline" size="sm" className="h-8"
                  onClick={() => navigate(`/radar?branch=${encodeURIComponent(r.branch)}`)}
                  data-testid={`lc-radar-${r.voter_id}`}>
                  <RadarIcon className="h-3.5 w-3.5 mr-1" />Radar
                </Button>
              </div>
              <div className="mt-2 grid gap-1.5">
                {r.diffs.map((d) => {
                  const fmt = label(d);
                  return (
                    <div key={d.field} className="flex flex-wrap items-center gap-2 text-xs">
                      <span className="text-muted-foreground w-24 shrink-0">{d.label}</span>
                      <Badge variant="outline" className="font-normal">Merkez: {fmt(d.central)}</Badge>
                      <span className="text-muted-foreground">≠</span>
                      <Badge className="bg-amber-600 hover:bg-amber-600 font-normal">Şube: {fmt(d.branch)}</Badge>
                    </div>
                  );
                })}
              </div>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={!!confirm} onOpenChange={(o) => { if (!o) { setConfirm(null); setApplyAll(false); } }}>
        <DialogContent className="max-w-md" data-testid="lc-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="text-base">
              {confirm === "central" ? "Merkezi esas al" : "Şubeyi esas al"}
            </DialogTitle>
            <DialogDescription className="text-xs">
              Seçili firmalarda merkez ve şube katmanı arasındaki fark kaldırılır.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 text-sm">
            <p><b>{applyAll ? (fieldCounts?.counts?.[resolveField] ?? 0) : selected.length}</b> firma eşitlenecek.</p>
            {applyAll && (
              <p className="text-xs font-medium" data-testid="lc-confirm-applyall">
                Kapsam: {branch === "all" ? "TÜM ŞUBELER" : branch} · {FIELD_LABELS[resolveField]} alanındaki
                tüm farklar (liste filtresinden bağımsız)
              </p>
            )}
            <p className="text-muted-foreground text-xs">
              {confirm === "central"
                ? "Seçili firmalarda şube katmanındaki farklı değerler silinir; merkez kaydı her iki ekranda da geçerli olur."
                : "Seçili firmalarda merkez kaydı şube katmanındaki değerle güncellenir, ardından şube katmanı farkı kaldırılır."}
            </p>
            <p className="text-xs rounded-md border border-amber-300 bg-amber-50 dark:bg-amber-950/30 dark:border-amber-800 px-2.5 py-2 text-amber-800 dark:text-amber-300">
              Kapsam: {resolveField === "all" ? "farklı tüm alanlar" : FIELD_LABELS[resolveField]} ·
              Bu işlem geri alınamaz, her değişiklik denetim kaydına yazılır.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setConfirm(null); setApplyAll(false); }} data-testid="lc-confirm-cancel">Vazgeç</Button>
            <Button onClick={doResolve} disabled={busy}
              className={confirm === "branch" ? "bg-amber-600 hover:bg-amber-700" : "bg-[#0F2C59] hover:bg-[#0F2C59]/90"}
              data-testid="lc-confirm-apply">
              {busy ? "Eşitleniyor..." : "Onayla ve eşitle"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
